import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Crown, Plus, Save, Trash2, Loader2, ToggleLeft, ToggleRight, X,
  Sparkles,
} from "lucide-react";

export default function AdminPlans() {
  const [data, setData] = useState({ plans: [], feature_keys: [], limit_keys: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/plans");
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const newPlan = () => setEditing({
    id: "", name: "", description: "", amount: 0, sort_order: data.plans.length + 1,
    features: Object.fromEntries(data.feature_keys.map((f) => [f.key, false])),
    limits: Object.fromEntries(data.limit_keys.map((l) => [l.key, 0])),
    active: true, _isNew: true,
  });

  return (
    <div className="space-y-6" data-testid="admin-plans-root">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2 flex items-center gap-2">
            <Crown className="h-3.5 w-3.5" /> Plans & Pricing
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Subscription plans · {data.plans.length}</h1>
          <p className="text-white/50 text-sm mt-2">Edit pricing, features, and per-plan usage limits. Changes apply to new subscriptions immediately.</p>
        </div>
        <button onClick={newPlan} data-testid="admin-plan-new"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90">
          <Plus className="h-4 w-4" /> New plan
        </button>
      </header>

      {loading ? <div className="h-64 glass rounded-2xl animate-pulse" /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.plans.map((p) => (
            <PlanCard key={p.id} plan={p} featureKeys={data.feature_keys} onEdit={() => setEditing({ ...p })} />
          ))}
          {data.plans.length === 0 && <div className="col-span-3 text-center text-sm text-white/40 py-12">No plans yet.</div>}
        </div>
      )}

      {editing && (
        <PlanEditor plan={editing}
          featureKeys={data.feature_keys} limitKeys={data.limit_keys}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function PlanCard({ plan, featureKeys, onEdit }) {
  const enabledCount = Object.values(plan.features || {}).filter(Boolean).length;
  return (
    <div className={`glass rounded-2xl p-5 tilt-card ${plan.active ? "" : "opacity-60"}`} data-testid={`plan-card-${plan.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-heading text-xl font-black">{plan.name}</div>
          <div className="text-xs text-white/40 font-mono">{plan.id}</div>
        </div>
        {!plan.active && <span className="text-[10px] bg-rose-500/15 text-rose-300 px-2 py-0.5 rounded uppercase tracking-[0.1em]">Hidden</span>}
      </div>
      <div className="font-heading font-black text-3xl mb-1">₹{(plan.amount || 0).toLocaleString("en-IN")}</div>
      <div className="text-xs text-white/40">/ 30 days · sort #{plan.sort_order ?? 0}</div>
      <div className="text-xs text-white/60 mt-3 leading-relaxed">{plan.description}</div>
      <div className="mt-4 text-xs">
        <div className="text-white/40 uppercase tracking-[0.2em] mb-1">Features · {enabledCount}/{featureKeys.length}</div>
        <div className="flex flex-wrap gap-1">
          {featureKeys.filter((f) => plan.features?.[f.key]).slice(0, 6).map((f) => (
            <span key={f.key} className="bg-indigo-500/15 text-indigo-200 px-2 py-0.5 rounded text-[10px]">{f.label}</span>
          ))}
          {enabledCount > 6 && <span className="text-white/40 text-[10px]">+{enabledCount - 6} more</span>}
        </div>
      </div>
      <button onClick={onEdit} data-testid={`plan-edit-${plan.id}`}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg glass text-xs hover:bg-white/5">
        Edit
      </button>
    </div>
  );
}

function PlanEditor({ plan, featureKeys, limitKeys, onClose, onSaved }) {
  const [form, setForm] = useState(plan);
  const [saving, setSaving] = useState(false);

  const tog = (k) => setForm({ ...form, features: { ...form.features, [k]: !form.features?.[k] } });
  const setLim = (k, v) => setForm({ ...form, limits: { ...form.limits, [k]: parseInt(v || 0, 10) } });

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const body = {
        id: form.id, name: form.name, description: form.description,
        amount: form.amount, sort_order: form.sort_order,
        features: form.features, limits: form.limits, active: form.active,
      };
      if (form._isNew) await api.post("/admin/plans", body);
      else await api.put(`/admin/plans/${plan.id}`, body);
      toast.success("Plan saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete plan "${plan.name}"? This is not reversible.`)) return;
    try {
      await api.delete(`/admin/plans/${plan.id}`);
      toast.success("Plan deleted");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const inp = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={onClose}>
      <div className="glass-strong border border-indigo-500/30 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="plan-editor">
        <div className="sticky top-0 z-10 bg-[#0a0a13]/95 backdrop-blur p-6 border-b border-white/5 flex items-center justify-between">
          <h3 className="font-heading text-2xl font-black">{form._isNew ? "New plan" : `Edit · ${form.name}`}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-white/40" /></button>
        </div>

        <div className="p-6 space-y-6">
          <section>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3">Basics</div>
            <div className="grid md:grid-cols-2 gap-3">
              <Lab label="Name *"><input className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="plan-name" /></Lab>
              <Lab label="ID (slug) — locked after create">
                <input className={inp + " font-mono"} value={form.id} disabled={!form._isNew}
                  onChange={(e) => setForm({ ...form, id: e.target.value.toLowerCase().replace(/\s+/g, "-") })}
                  placeholder="auto-generated from name" data-testid="plan-id" />
              </Lab>
              <Lab label="Amount (₹) *">
                <input type="number" className={inp + " font-mono"} value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: parseInt(e.target.value || 0, 10) })}
                  data-testid="plan-amount" />
              </Lab>
              <Lab label="Sort order">
                <input type="number" className={inp + " font-mono"} value={form.sort_order || 0}
                  onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || 0, 10) })}
                  data-testid="plan-sort" />
              </Lab>
              <Lab label="Description" full>
                <textarea rows={2} className={inp} value={form.description || ""}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="plan-description" />
              </Lab>
              <Lab label="Active (visible to customers)" full>
                <button onClick={() => setForm({ ...form, active: !form.active })}
                  data-testid="plan-active-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${form.active ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"} text-xs`}>
                  {form.active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                  {form.active ? "Active" : "Hidden"}
                </button>
              </Lab>
            </div>
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3">Features included</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {featureKeys.map((f) => {
                const on = !!form.features?.[f.key];
                return (
                  <button key={f.key} onClick={() => tog(f.key)}
                    data-testid={`plan-feature-${f.key}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-left text-xs transition ${on ? "bg-indigo-500/15 text-indigo-200 border border-indigo-500/40" : "glass hover:bg-white/5"}`}>
                    {on ? <ToggleRight className="h-4 w-4 text-indigo-300 shrink-0" /> : <ToggleLeft className="h-4 w-4 text-white/30 shrink-0" />}
                    <span className="flex-1">{f.label}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3">Usage limits <span className="text-white/30 normal-case tracking-normal">(0 = unlimited)</span></div>
            <div className="grid sm:grid-cols-2 gap-3">
              {limitKeys.map((l) => (
                <Lab key={l.key} label={l.label + (l.unit ? ` (${l.unit})` : "")}>
                  <input type="number" min={0} className={inp + " font-mono"} value={form.limits?.[l.key] ?? 0}
                    onChange={(e) => setLim(l.key, e.target.value)}
                    data-testid={`plan-limit-${l.key}`} />
                </Lab>
              ))}
            </div>
          </section>
        </div>

        <div className="sticky bottom-0 bg-[#0a0a13]/95 backdrop-blur p-4 border-t border-white/5 flex items-center justify-between">
          {!form._isNew && (
            <button onClick={remove} data-testid="plan-delete"
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs text-rose-300 hover:bg-rose-500/10">
              <Trash2 className="h-3 w-3" /> Delete plan
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg glass text-sm">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="plan-save"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save plan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Lab({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs text-white/40 uppercase tracking-[0.2em]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
